/**
 * @file apps/web/src/components/ScriptTable.tsx
 * @description Notion-style inline-editing table for UAT test scripts.
 * Click any cell to edit it in place. Steps have Add/Delete/Reorder controls.
 * Top-level field edits call onUpdate; step edits call onSaveStep/onUpdateStep/onDeleteStep/onMoveStep.
 */

import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ChevronDown, Plus, Trash2, ChevronUp as ChevUp, ChevronDown as ChevDn, Pencil, Check, X, Tag as TagIcon, Bug, AlertCircle, ShieldCheck } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScriptStep {
  id?:             string;
  step_number:     number;
  description:     string;
  expected_result?: string | null;
  notes?:          string | null;
  // Lightweight per-step result marker — independent of formal UAT session
  // execution (uat_results). Drives the "Log Defect" affordance below.
  last_result?:    string;
}

export interface LogDefectData {
  title:       string;
  description: string;
  severity:    string;
}

export interface StepFormData {
  action:          string;
  expected_result: string;
  test_data:       string;
}

export interface TagRef {
  id:    string;
  name:  string;
  color: string | null;
}

export interface TestScript {
  id:          string;
  title:       string;
  module:      string;
  status:      string;
  description?: string | null;
  steps?:      ScriptStep[];
  // Module tags — only rendered when `allTags` is passed to ScriptTable.
  tags?:       TagRef[];
}

interface ScriptTableProps {
  scripts:        TestScript[];
  loading?:       boolean;
  locked?:        boolean;
  onUpdate?:      (scriptId: string, field: string, value: string) => void;
  onDelete?:      (scriptId: string) => void;
  onAddScript?:   () => void;
  onSaveStep?:    (scriptId: string, data: StepFormData) => Promise<void>;
  onUpdateStep?:  (scriptId: string, stepId: string, field: string, value: string) => void;
  onDeleteStep?:  (scriptId: string, stepId: string) => void;
  onMoveStep?:    (scriptId: string, stepId: string, direction: 'up' | 'down') => void;
  // Module tag assignment — optional. When both are provided, expanded rows
  // show tag chips (assigned = removable, unassigned = "+ add").
  allTags?:       TagRef[];
  onToggleTag?:   (scriptId: string, tagId: string, hasTag: boolean) => void;
  // Defect logging from a step marked fail/blocked. Always links to the
  // script (scriptId), never the step.
  onLogDefect?:   (scriptId: string, data: LogDefectData) => Promise<void>;
  // Per-script "Submit for Approval" trigger (individual test_script entity
  // approval — distinct from the pack-level test_script_pack flow).
  onSubmitForApproval?: (scriptId: string) => void;
  // True when the script's subcycle already has an in-flight or approved
  // test_script_pack approval — disables the per-script button to avoid a
  // script being covered by both a pack approval and its own at once.
  packApprovalActive?: boolean;
  projectId?:     string;
  className?:     string;
}

// ---------------------------------------------------------------------------
// Status label map
// ---------------------------------------------------------------------------

export const STATUS_LABELS: Record<string, string> = {
  draft:       'Draft',
  in_review:   'In Review',
  ready:       'Ready to Test',
  in_progress: 'In Progress',
  passed:      'Passed',
  failed:      'Failed',
  archived:    'Archived',
};

const STATUS_OPTIONS = Object.keys(STATUS_LABELS) as (keyof typeof STATUS_LABELS)[];

// ---------------------------------------------------------------------------
// Step result label map (mirrors the DB's step_result enum)
// ---------------------------------------------------------------------------

export const RESULT_LABELS: Record<string, string> = {
  not_tested: 'Not Tested',
  pass:       'Pass',
  fail:       'Fail',
  blocked:    'Blocked',
  n_a:        'N/A',
};

const RESULT_OPTIONS = Object.keys(RESULT_LABELS);
const FAILING_RESULTS = new Set(['fail', 'blocked']);

// ---------------------------------------------------------------------------
// Editable cell
// ---------------------------------------------------------------------------

interface EditableCellProps {
  value:        string;
  placeholder?: string;
  onCommit:     (value: string) => void;
  multiline?:   boolean;
  className?:   string;
  tabIndex?:    number;
  locked?:      boolean;
}

function EditableCell({ value, placeholder, onCommit, multiline, className = '', tabIndex, locked = false }: EditableCellProps) {
  const [editing, setEditing]   = useState(false);
  const [draft, setDraft]       = useState(value);
  const [saved, setSaved]       = useState(false);
  const inputRef                = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const savedTimer              = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const startEdit = () => {
    if (locked) return;
    setDraft(value);
    setEditing(true);
    setSaved(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commit = () => {
    setEditing(false);
    if (draft !== value) {
      onCommit(draft);
      setSaved(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1500);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit(); }
    if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { setEditing(false); setDraft(value); }
    if (e.key === 'Tab') { commit(); }
  };

  if (!editing) {
    return (
      <div
        onClick={startEdit}
        tabIndex={tabIndex ?? 0}
        onFocus={startEdit}
        className={[
          'select-none rounded px-1 -mx-1 transition-colors whitespace-pre-wrap',
          locked ? 'cursor-default' : 'cursor-text hover:bg-sky/40',
          value ? 'text-ink' : 'text-ink/30 italic',
          saved ? 'ring-1 ring-success/40' : '',
          className,
        ].join(' ')}
        role="textbox"
        aria-label={placeholder}
        aria-readonly={locked}
      >
        {value || placeholder}
        {saved && (
          <span className="inline-block ml-1.5 text-[10px] font-mono font-semibold text-success align-middle">
            ✓ Saved
          </span>
        )}
      </div>
    );
  }

  const sharedProps = {
    ref:       inputRef as never,
    value:     draft,
    onChange:  (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    onBlur:    commit,
    onKeyDown: handleKeyDown,
    placeholder,
    tabIndex,
    className: [
      'w-full bg-sky/40 text-ink text-sm rounded px-1 outline-none ring-2 ring-blue/40 -mx-1',
      multiline ? 'resize-y min-h-[4rem]' : '',
      className,
    ].join(' '),
  };

  return multiline
    ? (
      <>
        <textarea {...sharedProps} rows={3} />
        <p className="text-[10px] font-mono text-muted mt-0.5">⌘↵ to save · Esc to cancel</p>
      </>
    )
    : <input {...sharedProps} type="text" />;
}

// ---------------------------------------------------------------------------
// Module options
// ---------------------------------------------------------------------------

const MODULES = [
  'finance','procurement','sales','inventory','manufacturing','hr','payroll','projects','assets','other',
];

// ---------------------------------------------------------------------------
// Inline Add Step Form
// ---------------------------------------------------------------------------

function AddStepForm({
  stepNumber,
  onSave,
  onCancel,
}: {
  stepNumber: number;
  onSave:     (data: StepFormData) => Promise<void>;
  onCancel:   () => void;
}) {
  const [action, setAction]               = useState('');
  const [expectedResult, setExpectedResult] = useState('');
  const [testData, setTestData]           = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const firstRef                          = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  const handleSave = async () => {
    if (!action.trim()) { setError('Action is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ action: action.trim(), expected_result: expectedResult.trim(), test_data: testData.trim() });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save step');
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };

  return (
    <tr className="bg-blue/5 border-t border-blue/10" onKeyDown={handleKeyDown}>
      {/* Step number */}
      <td className="table-cell w-9 pr-0 align-top pt-3">
        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue text-white text-[10px] font-mono font-bold mx-auto">
          {stepNumber}
        </span>
      </td>

      {/* Form fields */}
      <td className="table-cell" colSpan={3}>
        <div className="space-y-2 py-1">
          <div>
            <label className="text-[10px] font-mono font-semibold text-blue uppercase tracking-wide block mb-1">
              Action <span className="text-danger">*</span>
            </label>
            <textarea
              ref={firstRef}
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="What to do…"
              rows={2}
              className="w-full input text-sm resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono font-semibold text-muted uppercase tracking-wide block mb-1">
                Expected Result
              </label>
              <textarea
                value={expectedResult}
                onChange={(e) => setExpectedResult(e.target.value)}
                placeholder="What should happen…"
                rows={2}
                className="w-full input text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono font-semibold text-muted uppercase tracking-wide block mb-1">
                Test Data <span className="text-ink/30">(optional)</span>
              </label>
              <input
                type="text"
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                placeholder="Data to use…"
                className="input text-sm"
              />
            </div>
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary py-1.5 px-4 text-xs font-semibold flex items-center gap-1.5"
            >
              <Check size={12} /> {saving ? 'Saving…' : 'Save Step'}
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              className="btn-ghost py-1.5 px-3 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      </td>

      {/* Status + actions — empty for add form */}
      <td className="table-cell" />
      <td className="table-cell" />
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Log Defect modal — opened from a step marked fail/blocked. Always links
// the resulting defect to the script (test_script_id), never the step.
// ---------------------------------------------------------------------------

function LogDefectModal({
  open,
  scriptTitle,
  onSubmit,
  onClose,
}: {
  open:        boolean;
  scriptTitle: string;
  onSubmit:    (data: LogDefectData) => Promise<void>;
  onClose:     () => void;
}) {
  const [title, setTitle]       = useState('');
  const [description, setDesc]  = useState('');
  const [severity, setSeverity] = useState('medium');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (open) { setTitle(''); setDesc(''); setSeverity('medium'); setError(''); }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit({ title: title.trim(), description, severity });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to log defect');
    } finally {
      setSaving(false);
    }
  };

  // Portaled to document.body — this modal is triggered from within a
  // <tr>, and a fixed-position overlay div is not valid as a direct child
  // of a table row.
  return createPortal(
    <>
      <div
        onClick={() => !saving && onClose()}
        className="fixed inset-0 bg-ink/20 backdrop-blur-[2px] z-40 animate-[fade-in_150ms_ease]"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-label="Log Defect">
        <div className="bg-white rounded-2xl shadow-modal w-full max-w-md flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(8,17,31,0.07)]">
            <div className="flex items-center gap-2">
              <Bug size={16} className="text-danger" />
              <h2 className="font-display text-base font-semibold text-ink">Log Defect</h2>
            </div>
            <button onClick={onClose} disabled={saving} className="btn-icon text-muted hover:text-ink">
              <X size={18} />
            </button>
          </div>

          <div className="px-6 py-4 space-y-3">
            <div>
              <label className="label mb-1.5">Test Script</label>
              <div className="input text-sm bg-gray/60 text-ink/70 cursor-not-allowed">
                {scriptTitle}
              </div>
            </div>
            <div>
              <label className="label mb-1.5">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the defect"
                className="input text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="label mb-1.5">Severity</label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="select text-sm">
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="label mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Steps to reproduce, expected vs actual…"
                className="input text-sm min-h-[80px] resize-none"
                rows={3}
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-danger">
                <AlertCircle size={13} /> {error}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-[rgba(8,17,31,0.07)] flex justify-end gap-2">
            <button onClick={onClose} disabled={saving} className="btn-ghost text-sm">Cancel</button>
            <button onClick={() => void handleSubmit()} disabled={saving} className="btn-primary text-sm">
              {saving ? 'Logging…' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Step sub-row (existing step)
// ---------------------------------------------------------------------------

function StepRow({
  step,
  isFirst,
  isLast,
  scriptTitle,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onLogDefect,
  tabBase,
  locked = false,
}: {
  step:         ScriptStep;
  isFirst:      boolean;
  isLast:       boolean;
  scriptTitle:  string;
  onUpdate:     (field: string, value: string) => void;
  onDelete:     () => void;
  onMoveUp:     () => void;
  onMoveDown:   () => void;
  onLogDefect?: (data: LogDefectData) => Promise<void>;
  tabBase:      number;
  locked?:      boolean;
}) {
  const [showDefectModal, setShowDefectModal] = useState(false);
  const result = step.last_result ?? 'not_tested';
  const isFailing = FAILING_RESULTS.has(result);

  return (
    <tr className="bg-sky/20 hover:bg-sky/30 transition-colors group/step">
      {/* Step number + reorder */}
      <td className="table-cell w-9 pr-0">
        <div className="flex flex-col items-center gap-0.5 opacity-0 group-hover/step:opacity-100 transition-opacity">
          <button
            onClick={onMoveUp}
            disabled={isFirst || locked}
            className="btn-icon w-4 h-4 text-muted disabled:opacity-20"
            title="Move up"
          >
            <ChevUp size={10} />
          </button>
          <span className="font-mono text-[10px] text-muted">{step.step_number}</span>
          <button
            onClick={onMoveDown}
            disabled={isLast || locked}
            className="btn-icon w-4 h-4 text-muted disabled:opacity-20"
            title="Move down"
          >
            <ChevDn size={10} />
          </button>
        </div>
        {/* Always-visible step number when not hovering */}
        <span className="group-hover/step:hidden font-mono text-[10px] text-muted flex items-center justify-center">
          {step.step_number}
        </span>
      </td>

      {/* Action / description */}
      <td className="table-cell pl-4">
        <p className="text-[10px] font-mono text-muted mb-0.5">Action</p>
        <EditableCell
          value={step.description}
          placeholder="What to do…"
          onCommit={(v) => onUpdate('description', v)}
          multiline
          tabIndex={tabBase}
          className="text-sm"
          locked={locked}
        />
      </td>

      {/* Expected result */}
      <td className="table-cell">
        <p className="text-[10px] font-mono text-muted mb-0.5">Expected Result</p>
        <EditableCell
          value={step.expected_result ?? ''}
          placeholder="What should happen…"
          onCommit={(v) => onUpdate('expected_result', v)}
          multiline
          tabIndex={tabBase + 1}
          className="text-sm text-ink/80"
          locked={locked}
        />
      </td>

      {/* Notes / test data */}
      <td className="table-cell">
        <p className="text-[10px] font-mono text-muted mb-0.5">Test Data</p>
        <EditableCell
          value={step.notes ?? ''}
          placeholder="Data to use…"
          onCommit={(v) => onUpdate('notes', v)}
          tabIndex={tabBase + 2}
          className="text-sm text-ink/60"
          locked={locked}
        />
      </td>

      {/* Result */}
      <td className="table-cell">
        <select
          value={result}
          onChange={(e) => onUpdate('last_result', e.target.value)}
          disabled={locked}
          className={[
            'text-xs bg-transparent border-0 outline-none cursor-pointer',
            'hover:text-ink focus:ring-2 focus:ring-blue/30 rounded px-1 py-0.5',
            isFailing ? 'text-danger font-medium' : 'text-ink/60',
          ].join(' ')}
        >
          {RESULT_OPTIONS.map((r) => (
            <option key={r} value={r}>{RESULT_LABELS[r]}</option>
          ))}
        </select>
      </td>

      {/* Log Defect (shown once the step is fail/blocked) + Delete */}
      <td className="table-cell text-right">
        <div className="flex items-center justify-end gap-1.5">
          {isFailing && onLogDefect && (
            <button
              onClick={() => setShowDefectModal(true)}
              className="flex items-center gap-1 text-[10px] font-mono font-semibold text-danger hover:text-danger/80 transition-colors px-1.5 py-0.5 rounded border border-danger/30 hover:bg-danger/5"
              title="Log a defect against this test script"
            >
              <Bug size={11} /> Log Defect
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={locked}
            className="btn-icon w-6 h-6 text-muted opacity-0 group-hover/step:opacity-100 transition-opacity hover:text-danger disabled:opacity-0 disabled:cursor-not-allowed"
            title="Delete step"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </td>

      {onLogDefect && (
        <LogDefectModal
          open={showDefectModal}
          scriptTitle={scriptTitle}
          onSubmit={(data) => onLogDefect(data)}
          onClose={() => setShowDefectModal(false)}
        />
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Script row (expandable)
// ---------------------------------------------------------------------------

function ScriptRow({
  script,
  onUpdate,
  onDelete,
  onSaveStep,
  onUpdateStep,
  onDeleteStep,
  onMoveStep,
  allTags,
  onToggleTag,
  onLogDefect,
  rowIdx,
  locked = false,
  onSubmitForApproval,
  packApprovalActive = false,
}: {
  script:         TestScript;
  onUpdate?:      (id: string, field: string, value: string) => void;
  onDelete?:      (id: string) => void;
  onSaveStep?:    (id: string, data: StepFormData) => Promise<void>;
  onUpdateStep?:  (scriptId: string, stepId: string, field: string, value: string) => void;
  onDeleteStep?:  (scriptId: string, stepId: string) => void;
  onMoveStep?:    (scriptId: string, stepId: string, direction: 'up' | 'down') => void;
  allTags?:       TagRef[];
  onToggleTag?:   (scriptId: string, tagId: string, hasTag: boolean) => void;
  onLogDefect?:   (scriptId: string, data: LogDefectData) => Promise<void>;
  rowIdx:         number;
  locked?:        boolean;
  onSubmitForApproval?: (scriptId: string) => void;
  packApprovalActive?:  boolean;
}) {
  const [expanded, setExpanded]   = useState(false);
  const [addingStep, setAddingStep] = useState(false);
  const steps = [...(script.steps ?? [])].sort((a, b) => a.step_number - b.step_number);
  const tabBase = rowIdx * 100;

  const handleSaveStep = async (data: StepFormData) => {
    if (onSaveStep) {
      await onSaveStep(script.id, data);
    }
    setAddingStep(false);
  };

  return (
    <>
      <tr className="table-row group">
        {/* Expand toggle */}
        <td className="table-cell w-9 pr-0">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="btn-icon w-6 h-6 text-muted"
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </td>

        {/* Title */}
        <td className="table-cell min-w-[260px]">
          <EditableCell
            value={script.title}
            placeholder="Script title…"
            onCommit={(v) => onUpdate?.(script.id, 'title', v)}
            tabIndex={tabBase + 10}
            className="font-medium text-sm"
            locked={locked}
          />
          {script.description && (
            <p className="text-[11px] text-muted mt-0.5 truncate max-w-xs">
              {script.description}
            </p>
          )}
        </td>

        {/* First step action preview */}
        <td className="table-cell text-sm text-ink/70">
          {steps[0]?.description ?? <span className="text-muted/50">—</span>}
        </td>

        {/* Module */}
        <td className="table-cell">
          <select
            value={script.module}
            onChange={(e) => onUpdate?.(script.id, 'module', e.target.value)}
            disabled={locked}
            className="text-xs font-mono text-ink/70 bg-transparent border-0 outline-none cursor-pointer
                       hover:text-ink focus:ring-2 focus:ring-blue/30 rounded px-1 py-0.5 -mx-1"
          >
            {MODULES.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </td>

        {/* Status */}
        <td className="table-cell">
          <select
            value={script.status}
            onChange={(e) => onUpdate?.(script.id, 'status', e.target.value)}
            disabled={locked}
            className="text-xs bg-transparent border-0 outline-none cursor-pointer
                       hover:text-ink focus:ring-2 focus:ring-blue/30 rounded px-1 py-0.5"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </td>

        {/* Steps count + delete */}
        <td className="table-cell text-right">
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs font-mono text-muted">{steps.length} steps</span>
            {onSubmitForApproval && !locked && script.status !== 'in_review' && !packApprovalActive && (
              <button
                onClick={() => onSubmitForApproval(script.id)}
                className="btn-icon w-6 h-6 text-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-blue"
                title="Submit for Approval"
              >
                <ShieldCheck size={12} />
              </button>
            )}
            {onDelete && !locked && (
              <button
                onClick={() => onDelete(script.id)}
                className="btn-icon w-6 h-6 text-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded step rows */}
      {expanded && steps.map((step, i) => (
        <StepRow
          key={step.id ?? i}
          step={step}
          isFirst={i === 0}
          isLast={i === steps.length - 1}
          scriptTitle={script.title}
          onUpdate={(field, value) => {
            if (onUpdateStep && step.id) onUpdateStep(script.id, step.id, field, value);
          }}
          onDelete={() => {
            if (onDeleteStep && step.id) onDeleteStep(script.id, step.id);
          }}
          onMoveUp={() => {
            if (onMoveStep && step.id) onMoveStep(script.id, step.id, 'up');
          }}
          onMoveDown={() => {
            if (onMoveStep && step.id) onMoveStep(script.id, step.id, 'down');
          }}
          onLogDefect={onLogDefect ? (data) => onLogDefect(script.id, data) : undefined}
          tabBase={tabBase + 20 + i * 10}
          locked={locked}
        />
      ))}

      {/* Inline add-step form */}
      {expanded && addingStep && (
        <AddStepForm
          stepNumber={steps.length + 1}
          onSave={handleSaveStep}
          onCancel={() => setAddingStep(false)}
        />
      )}

      {/* Add Step button row (shown when expanded, form not open) */}
      {expanded && !addingStep && !locked && (
        <tr className="bg-sky/10">
          <td colSpan={6} className="table-cell pl-10">
            <button
              onClick={() => setAddingStep(true)}
              className="flex items-center gap-1.5 text-xs text-blue/70 hover:text-blue transition-colors font-medium py-1"
            >
              <Plus size={12} /> Add step
            </button>
          </td>
        </tr>
      )}

      {/* Tag chips — only rendered when the caller supplies allTags */}
      {expanded && allTags && allTags.length > 0 && (
        <tr className="bg-sky/10 border-t border-[rgba(8,17,31,0.05)]">
          <td colSpan={6} className="table-cell pl-10 py-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <TagIcon size={11} className="text-muted shrink-0" />
              {(script.tags ?? []).map((t) => (
                <button
                  key={t.id}
                  onClick={() => onToggleTag?.(script.id, t.id, true)}
                  className="flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full"
                  style={{ background: (t.color ?? '#0055FF') + '1F', color: t.color ?? '#0055FF' }}
                  title="Remove tag"
                >
                  {t.name} <X size={9} />
                </button>
              ))}
              {allTags.filter((t) => !(script.tags ?? []).some((st) => st.id === t.id)).map((t) => (
                <button
                  key={t.id}
                  onClick={() => onToggleTag?.(script.id, t.id, false)}
                  className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-dashed border-[rgba(8,17,31,0.15)] text-muted/60 hover:text-blue hover:border-blue transition-colors"
                >
                  + {t.name}
                </button>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TableSkeleton() {
  return (
    <div className="animate-pulse">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex gap-4 px-4 py-3 border-b border-[rgba(8,17,31,0.05)]">
          <div className="w-5 h-5 rounded bg-gray" />
          <div className="flex-1 h-4 rounded bg-gray" />
          <div className="w-32 h-4 rounded bg-gray" />
          <div className="w-20 h-4 rounded bg-gray" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScriptTable
// ---------------------------------------------------------------------------

export function ScriptTable({
  scripts,
  loading,
  locked = false,
  onUpdate,
  onDelete,
  onAddScript,
  onSaveStep,
  onUpdateStep,
  onDeleteStep,
  onMoveStep,
  allTags,
  onToggleTag,
  onLogDefect,
  onSubmitForApproval,
  packApprovalActive,
  className = '',
}: ScriptTableProps) {
  if (loading) {
    return (
      <div className={['card overflow-hidden', className].join(' ')}>
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div className={['card overflow-hidden', className].join(' ')}>
      {locked && (
        <div className="px-4 py-2.5 border-b border-warning/20 bg-warning/6 text-sm text-ink flex items-center gap-2">
          Locked — this test script pack is approved.
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="table-header w-9 pr-0" />
            <th className="table-header">Title / Steps</th>
            <th className="table-header">First Step Action</th>
            <th className="table-header w-28">Module</th>
            <th className="table-header w-32">Status</th>
            <th className="table-header w-28 text-right">Steps</th>
          </tr>
        </thead>
        <tbody>
          {scripts.map((script, i) => (
            <ScriptRow
              key={script.id}
              script={script}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onSaveStep={onSaveStep}
              onUpdateStep={onUpdateStep}
              onDeleteStep={onDeleteStep}
              onMoveStep={onMoveStep}
              allTags={allTags}
              onToggleTag={onToggleTag}
              onLogDefect={onLogDefect}
              rowIdx={i}
              locked={locked}
              onSubmitForApproval={onSubmitForApproval}
              packApprovalActive={packApprovalActive}
            />
          ))}
        </tbody>
      </table>

      {scripts.length === 0 && (
        <div className="empty-state">
          <p className="text-sm text-muted">No test scripts yet.</p>
          {onAddScript && !locked && (
            <button onClick={onAddScript} className="btn-secondary mt-4">
              <Plus size={14} /> Add First Script
            </button>
          )}
        </div>
      )}

      {onAddScript && scripts.length > 0 && !locked && (
        <div className="px-4 py-2 border-t border-[rgba(8,17,31,0.06)]">
          <button
            onClick={onAddScript}
            className="flex items-center gap-1.5 text-xs text-muted hover:text-blue transition-colors font-medium"
          >
            <Plus size={13} /> Add script
          </button>
        </div>
      )}
    </div>
  );
}
